import React from 'react';
import { CreditCard, Landmark, ArrowRight, ShieldCheck, PieChart, Coins, Building, Handshake, BarChart3, CheckCircle } from 'lucide-react';

const SectionTitle = ({ subtitle, title, align = 'left' }) => (
  <div className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}>
    <div className={`flex items-center gap-2 mb-3 ${align === 'center' ? 'justify-center' : ''}`}>
      <div className="w-12 h-0.5 bg-[#F49611]"></div>
      <span className="text-[#F49611] font-medium uppercase tracking-wider text-sm">{subtitle}</span>
    </div>
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">{title}</h2>
  </div>
);

const ServiceCard = ({ Icon, title, description, index }) => (
  <div className="group">
    <div className="relative p-8 rounded-xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="absolute top-0 right-0 w-24 h-24 bg-[#E8EEF6] rounded-bl-full -z-10 group-hover:bg-[#F49611] transition-colors duration-300"></div>
      <div className="flex items-start gap-6">
        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-[#E8EEF6] group-hover:bg-[#F49611] transition-colors duration-300 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#113262] group-hover:text-white transition-colors duration-300" />
          </div>
          <span className="absolute -bottom-4 -right-4 text-4xl font-bold text-[#E8EEF6] group-hover:text-[#F49611]/10 transition-colors duration-300">
            {String(index).padStart(2, '0')}
          </span>
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-[#113262] mb-3">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  </div>
);

const MainContent = () => {
  return (
    <div className="pt-12">
      {/* Intro Section */}
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <SectionTitle 
              subtitle="Who We Are"
              title="Your Gateway to Private Credit Opportunities"
            />
            <p className="text-lg text-gray-600 mb-8">
              With India's private credit market projected to reach ₹5 lakh crore by 2030, we provide sophisticated investors 
              access to this rapidly growing asset class. Our team conducts thorough credit analysis and due diligence 
              to identify high-quality private lending opportunities across the risk-return spectrum, from senior secured 
              loans to venture debt in emerging companies.
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="text-3xl font-bold text-[#113262] mb-2">12-15%</div>
                <div className="text-gray-600">Target Annual Returns</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#113262] mb-2">₹300Cr+</div>
                <div className="text-gray-600">Credit Assets Advised</div>
              </div>
            </div>
          </div>
          <div className="bg-[#E8EEF6] rounded-2xl p-8 md:p-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-[#113262] mb-6">Why Choose Us</h3>
              {[
                "Access to institutional-quality private credit opportunities",
                "Enhanced yields over traditional fixed income",
                "Strong downside protection through secured structures",
                "Diversification away from public market volatility"
              ].map((point, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#F49611] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        <SectionTitle 
          subtitle="Our Offerings"
          title="Comprehensive Private Credit Solutions"
        />
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              icon: Coins,
              title: "Venture Debt",
              description: "Lending solutions for early-stage companies with demonstrated traction, providing growth capital with equity upside potential through warrants"
            },
            {
              icon: Building,
              title: "Special Situations Financing",
              description: "Customized credit solutions for mid-size companies in transition phases such as private equity exits, acquisitions, or debt consolidation"
            },
            {
              icon: PieChart,
              title: "Structured Credit Products",
              description: "Investment opportunities in diversified pools of private loans with targeted risk-return profiles and structured protection mechanisms"
            },
            {
              icon: Handshake,
              title: "Direct Lending Partnerships",
              description: "Access to Category II AIFs specializing in direct lending to Indian businesses across sectors with strong collateral and covenant protection"
            }
          ].map((service, index) => (
            <ServiceCard 
              key={index}
              Icon={service.icon}
              title={service.title}
              description={service.description}
              index={index + 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainContent;